'use client';

import { AlertTriangle } from 'lucide-react';

import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { usePasswordBreachCheck } from '../hooks/use-password-breach-check';

/**
 * Advisory, non-blocking breach warning for the password forms. Renders only
 * when the live HaveIBeenPwned check (see usePasswordBreachCheck) flags the
 * typed password as breached. It never gates submit — a breached-but-policy-
 * valid password still signs up, matching the server (HIBP disabled).
 */
export function PasswordBreachWarning({
  password,
  className,
}: {
  password: string;
  className?: string;
}) {
  const { status } = usePasswordBreachCheck(password);

  if (status !== 'breached') {
    return null;
  }

  return (
    <div
      role="status"
      data-test="password-breach-warning"
      className={cn(
        'flex items-start gap-2 text-xs text-amber-600 dark:text-amber-500',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        <Trans i18nKey="auth:passwordChecklist.breachWarning" />
      </span>
    </div>
  );
}
